import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Menu, X, Phone, Instagram } from 'lucide-react';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Scroll to top on route change
    window.scrollTo(0, 0);
    setIsMobileMenuOpen(false);
  }, [location]);

  const navItems = [
    { name: 'בית', path: '/' },
    { name: 'קבוצות', path: '/groups' },
    { name: 'סדנאות', path: '/workshops' },
    { name: 'ליווי אישי', path: '/personal' },
    { name: 'המלצות', path: '/testimonials' },
    { name: 'בהזמנה אישית', path: '/custom-order' },
    { name: 'יצירת קשר', path: '/contact' },
  ];

  return (
    <motion.header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white bg-opacity-95 backdrop-blur-lg shadow-lg'
          : 'bg-gradient-to-b from-nitzan-light-bg/90 to-transparent backdrop-blur-sm'
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <nav className="container-width py-4">
        <div className="flex items-center justify-between">
          {/* Logo - Right side */}
          <Link to="/">
            <motion.div
              className="flex items-center gap-3 cursor-pointer group order-3 lg:order-1"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <img 
                src="/images/logo.png" 
                alt="ניצן שרף בוסל" 
                className="w-12 h-12 object-contain"
              />
              <span className={`text-xl md:text-2xl font-bold transition-colors ${
                isScrolled ? 'gradient-text' : 'text-nitzan-teal'
              }`}>
                ניצן שרף בוסל
              </span>
            </motion.div>
          </Link>

          {/* Desktop Navigation - Center */}
          <div className="hidden lg:flex items-center gap-1 order-2">
            {navItems.map((item, index) => (
              <Link key={index} to={item.path}>
                <motion.div
                  className={`px-4 py-2 rounded-full font-medium transition-all duration-300 ${
                    location.pathname === item.path
                      ? 'bg-nitzan-mint bg-opacity-30 text-nitzan-teal'
                      : isScrolled
                      ? 'text-nitzan-terracotta hover:bg-nitzan-mint hover:bg-opacity-20 hover:text-nitzan-teal'
                      : 'text-nitzan-teal hover:bg-white hover:bg-opacity-50'
                  }`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {item.name}
                </motion.div>
              </Link>
            ))}
          </div>

          {/* Contact Info - Left side */}
          <div className="hidden lg:flex items-center gap-4 order-1 lg:order-3">
            <motion.a
              href="tel:052-3918877"
              className="flex items-center gap-2 text-nitzan-teal hover:text-nitzan-mint transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Phone size={20} />
              <span className="font-medium">052-3918877</span>
            </motion.a>
            <motion.a
              href="https://instagram.com/nitzan_sb"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-nitzan-teal hover:text-nitzan-mint transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Instagram size={20} />
            </motion.a>
          </div>

          {/* Mobile Menu Button */}
          <motion.button
            className="lg:hidden p-2 rounded-full hover:bg-nitzan-mint hover:bg-opacity-30 transition-colors order-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            whileTap={{ scale: 0.9 }}
          >
            {isMobileMenuOpen ? (
              <X className="text-nitzan-teal" size={28} />
            ) : (
              <Menu className="text-nitzan-teal" size={28} />
            )}
          </motion.button>
        </div>

        {/* Mobile Menu */}
        <motion.div
          className={`lg:hidden overflow-hidden ${isMobileMenuOpen ? 'block' : 'hidden'}`}
          initial={{ opacity: 0, height: 0 }}
          animate={{
            opacity: isMobileMenuOpen ? 1 : 0,
            height: isMobileMenuOpen ? 'auto' : 0,
          }}
          transition={{ duration: 0.3 }}
        >
          <div className="pt-4 pb-2 space-y-2">
            {navItems.map((item, index) => (
              <Link key={index} to={item.path}>
                <motion.div
                  className={`block px-4 py-3 text-center rounded-2xl transition-all duration-300 font-medium ${
                    location.pathname === item.path
                      ? 'bg-nitzan-mint bg-opacity-30 text-nitzan-teal'
                      : 'text-nitzan-terracotta hover:bg-nitzan-mint hover:bg-opacity-30 hover:text-nitzan-teal'
                  }`}
                  initial={{ x: -50, opacity: 0 }}
                  animate={{
                    x: isMobileMenuOpen ? 0 : -50,
                    opacity: isMobileMenuOpen ? 1 : 0,
                  }}
                  transition={{ delay: index * 0.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {item.name}
                </motion.div>
              </Link>
            ))}
            <div className="flex justify-center gap-6 pt-4">
              <motion.a
                href="tel:052-3918877"
                className="flex items-center gap-2 text-nitzan-teal"
                whileTap={{ scale: 0.95 }}
              >
                <Phone size={20} />
                <span>052-3918877</span>
              </motion.a>
              <motion.a
                href="https://instagram.com/nitzan_sb"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-nitzan-teal"
                whileTap={{ scale: 0.95 }}
              >
                <Instagram size={20} />
                <span>@nitzan_sb</span>
              </motion.a>
            </div>
          </div>
        </motion.div>
      </nav>
    </motion.header>
  );
};

export default Header;

